import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
} from "antd";
import { ExpandOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  CHARGE_ITEM_CODES,
  CURRENCIES,
  OLIVEEB_APP_OPTIONS,
} from "components/finance/charges/ChargesConfigPage";
import useUsers from "hooks/userManagement/useUsers";

const { RangePicker } = DatePicker;
const DRAWER_WIDTH = 480;
const COUPON_CODE_PATTERN = /^[A-Z0-9]{8}$/;

const resolveCouponId = (coupon) => coupon?.id || coupon?.couponId || coupon?.code;
const resolveRestrictedUserIds = (coupon) =>
  coupon?.restrictedUserIds ||
  coupon?.eligibleUserIds ||
  coupon?.allowedUserIds ||
  [];

const resolveRestrictedUserOptions = (coupon) => {
  const users =
    coupon?.restrictedUsers ||
    coupon?.eligibleUsers ||
    coupon?.allowedUsers ||
    [];

  if (!Array.isArray(users)) return [];

  return users
    .map((user) => {
      const id = user?.id || user?.userId;
      if (!id) return null;
      const name =
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        user.fullName ||
        user.name ||
        user.email ||
        id;
      return {
        value: id,
        label: `${name}${user.email ? ` (${user.email})` : ""}`,
      };
    })
    .filter(Boolean);
};

const resolveDiscountValue = (coupon) =>
  coupon?.discountValue ?? coupon?.value ?? coupon?.amount;

const resolveStartsAt = (coupon) =>
  coupon?.startsAt || coupon?.validFrom || coupon?.effectiveFrom;

const resolveExpiresAt = (coupon) =>
  coupon?.expiresAt || coupon?.validUntil || coupon?.effectiveTo;

export default function CouponDrawer({ open, coupon, onClose, onSubmit, onCheckCode }) {
  const [form] = Form.useForm();
  const [isExpanded, setIsExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [discountType, setDiscountType] = useState("PERCENTAGE");
  const [availability, setAvailability] = useState("SHAREABLE");
  const [userOptions, setUserOptions] = useState([]);
  const [codeAvailability, setCodeAvailability] = useState({
    code: "",
    status: "idle",
    message: "",
  });
  const codeCheckSequence = useRef(0);
  const { searchUserProfiles, loading: usersLoading } = useUsers(0, 20, null, true, {
    autoFetch: false,
  });

  useEffect(() => {
    if (!open) return;

    if (coupon) {
      const startsAt = resolveStartsAt(coupon);
      const expiresAt = resolveExpiresAt(coupon);
      const nextDiscountType = coupon.discountType || coupon.valueType || "PERCENTAGE";
      const restrictedUserIds = resolveRestrictedUserIds(coupon);
      const restrictedUserOptions = resolveRestrictedUserOptions(coupon);
      const nextAvailability =
        restrictedUserIds.length || coupon.restrictedToUsers === true
          ? "RESTRICTED"
          : "SHAREABLE";

      form.setFieldsValue({
        code: coupon.code,
        name: coupon.name,
        description: coupon.description,
        discountType: nextDiscountType,
        discountValue:
          nextDiscountType === "PERCENTAGE" && Number(resolveDiscountValue(coupon)) <= 1
            ? Number(resolveDiscountValue(coupon)) * 100
            : resolveDiscountValue(coupon),
        currency: coupon.currency || "USD",
        minAmount: coupon.minAmount ?? coupon.minimumAmount,
        minChargeAmount: coupon.minChargeAmount,
        maxDiscountAmount: coupon.maxDiscountAmount,
        usageLimit: coupon.usageLimit ?? coupon.maxRedemptions,
        perUserLimit: coupon.perUserLimit,
        status: coupon.status || "ACTIVE",
        type: coupon.type || "USER",
        duration: coupon.duration || "ONE_TIME",
        app: coupon.app || coupon.clientApp,
        chargeCode: coupon.chargeCode || coupon.chargeItemCode,
        stackable: coupon.stackable ?? coupon.isStackable ?? false,
        availability: nextAvailability,
        restrictedUserIds,
        validRange: [
          startsAt ? dayjs(startsAt) : null,
          expiresAt ? dayjs(expiresAt) : null,
        ],
      });
      setDiscountType(nextDiscountType);
      setAvailability(nextAvailability);
      setUserOptions(restrictedUserOptions);
      setCodeAvailability({ code: coupon.code || "", status: "idle", message: "" });
    } else {
      form.resetFields();
      form.setFieldsValue({
        discountType: "PERCENTAGE",
        currency: "USD",
        status: "ACTIVE",
        type: "USER",
        duration: "ONE_TIME",
        app: "OLIVEEB_PAYMENT_SERVICE",
        stackable: false,
        availability: "SHAREABLE",
        restrictedUserIds: [],
      });
      setDiscountType("PERCENTAGE");
      setAvailability("SHAREABLE");
      setUserOptions([]);
      setCodeAvailability({ code: "", status: "idle", message: "" });
    }
  }, [coupon, form, open]);

  const handleClose = () => {
    form.resetFields();
    setCodeAvailability({ code: "", status: "idle", message: "" });
    onClose?.();
  };

  const handleFinish = async (values) => {
    const [startsAt, expiresAt] = values.validRange || [];
    const numericDiscount = Number(values.discountValue);
    const normalizedCode = values.code?.trim().toUpperCase() || undefined;
    const isRestricted = values.availability === "RESTRICTED";
    const restrictedUserIds = isRestricted
      ? Array.isArray(values.restrictedUserIds)
        ? values.restrictedUserIds
        : []
      : [];

    const payload = {
      ...(normalizedCode ? { code: normalizedCode } : {}),
      name: values.name?.trim(),
      description: values.description,
      discountType: values.discountType,
      discountValue: numericDiscount,
      currency: values.currency,
      minChargeAmount: values.minChargeAmount,
      maxDiscountAmount: values.maxDiscountAmount,
      usageLimit: values.usageLimit,
      perUserLimit: values.perUserLimit,
      status: values.status,
      type: values.type,
      duration: values.duration,
      app: values.app,
      chargeCode: values.chargeCode,
      stackable: values.stackable,
      shareable: !isRestricted,
      restrictedToUsers: isRestricted,
      restrictedUserIds,
      startsAt: startsAt ? startsAt.startOf("minute").toISOString() : null,
      expiresAt: expiresAt ? expiresAt.endOf("minute").toISOString() : null,
    };

    try {
      setSubmitting(true);
      await onSubmit(resolveCouponId(coupon), payload);
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const normalizeCodeInput = (event) =>
    String(event?.target?.value || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 8);

  const handleUserSearch = async (value) => {
    const query = String(value || "").trim();
    if (query.length < 3) {
      setUserOptions([]);
      return;
    }

    try {
      const users = (await searchUserProfiles(query)) || [];
      setUserOptions(
        users.map((user) => {
          const name =
            [user.firstName, user.lastName].filter(Boolean).join(" ") ||
            user.fullName ||
            user.email ||
            user.id;
          return {
            value: user.id,
            label: `${name}${user.email ? ` (${user.email})` : ""}`,
          };
        }),
      );
    } catch {
      setUserOptions([]);
    }
  };

  const checkCodeAvailability = async (normalizedCode) => {
    if (!normalizedCode || normalizedCode.length < 8) {
      setCodeAvailability({ code: normalizedCode, status: "idle", message: "" });
      return;
    }

    if (!COUPON_CODE_PATTERN.test(normalizedCode)) {
      setCodeAvailability({
        code: normalizedCode,
        status: "unavailable",
        message: "Use 8 uppercase letters and numbers.",
      });
      return;
    }

    const originalCode = String(coupon?.code || "").trim().toUpperCase();
    if (originalCode && originalCode === normalizedCode) {
      setCodeAvailability({
        code: normalizedCode,
        status: "available",
        message: "Code is available.",
      });
      return;
    }

    if (!onCheckCode) {
      setCodeAvailability({ code: normalizedCode, status: "idle", message: "" });
      return;
    }

    const sequence = codeCheckSequence.current + 1;
    codeCheckSequence.current = sequence;
    setCodeAvailability({
      code: normalizedCode,
      status: "checking",
      message: "Checking code...",
    });

    try {
      const result = await onCheckCode(normalizedCode, resolveCouponId(coupon));
      if (sequence !== codeCheckSequence.current) return;

      setCodeAvailability({
        code: normalizedCode,
        status: result?.available === false ? "unavailable" : "available",
        message:
          result?.available === false
            ? "Code is not available."
            : "Code is available.",
      });
    } catch {
      if (sequence !== codeCheckSequence.current) return;
      setCodeAvailability({
        code: normalizedCode,
        status: "unavailable",
        message: "Unable to verify coupon code.",
      });
    }
  };

  const handleFormValuesChange = (changedValues) => {
    if (Object.prototype.hasOwnProperty.call(changedValues, "availability")) {
      setAvailability(changedValues.availability || "SHAREABLE");
      if (changedValues.availability !== "RESTRICTED") {
        form.setFieldValue("restrictedUserIds", []);
      }
    }

    if (!Object.prototype.hasOwnProperty.call(changedValues, "code")) return;
    const normalizedCode = String(changedValues.code || "").trim().toUpperCase();

    if (normalizedCode.length === 8) {
      checkCodeAvailability(normalizedCode);
    } else {
      codeCheckSequence.current += 1;
      setCodeAvailability({ code: normalizedCode, status: "idle", message: "" });
    }
  };

  const validateCouponCode = async (_, value) => {
    const normalizedCode = String(value || "").trim().toUpperCase();
    if (!normalizedCode) return Promise.resolve();

    if (!COUPON_CODE_PATTERN.test(normalizedCode)) {
      return Promise.reject(
        new Error("Use exactly 8 uppercase letters and numbers."),
      );
    }

    const originalCode = String(coupon?.code || "").trim().toUpperCase();
    if (originalCode && originalCode === normalizedCode) {
      return Promise.resolve();
    }

    if (
      normalizedCode.length === 8 &&
      codeAvailability.code === normalizedCode &&
      codeAvailability.status === "unavailable"
    ) {
      return Promise.reject(new Error(codeAvailability.message || "Code is not available."));
    }

    if (
      normalizedCode.length === 8 &&
      codeAvailability.code === normalizedCode &&
      codeAvailability.status === "available"
    ) {
      return Promise.resolve();
    }

    if (!onCheckCode) return Promise.resolve();

    try {
      const result = await onCheckCode(normalizedCode, resolveCouponId(coupon));
      if (result?.available === false) {
        return Promise.reject(new Error("This coupon code is already in use."));
      }
    } catch {
      return Promise.reject(new Error("Unable to verify coupon code."));
    }

    return Promise.resolve();
  };

  return (
    <Drawer
      className="coupon-drawer"
      placement="right"
      width={isExpanded ? 760 : DRAWER_WIDTH}
      open={open}
      title={coupon ? "Edit Coupon" : "Create Coupon"}
      closable
      onClose={handleClose}
      destroyOnHidden
      zIndex={9999}
      maskClosable={!submitting}
      extra={
        <Tooltip title={isExpanded ? "Compact view" : "Expand"}>
          <Button
            type="text"
            size="small"
            icon={<ExpandOutlined />}
            onClick={() => setIsExpanded((prev) => !prev)}
          />
        </Tooltip>
      }
      styles={{
        header: { padding: "12px 16px" },
        body: { padding: 16 },
        footer: { padding: "10px 16px" },
      }}
      footer={
        <Space style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button size="small" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            loading={submitting}
            disabled={submitting}
            onClick={() => form.submit()}
          >
            {coupon ? "Save Changes" : "Create Coupon"}
          </Button>
        </Space>
      }
    >
      <style>
        {`
          .coupon-drawer .ant-drawer-title {
            font-size: 14px;
            font-weight: 700;
            color: #111827;
          }
          .coupon-drawer .ant-form-item {
            margin-bottom: 12px;
          }
          .coupon-drawer .ant-form-item-label > label {
            font-size: 12px;
            height: 20px;
            color: #4b5563;
          }
          .coupon-drawer .coupon-code-success .ant-form-item-explain,
          .coupon-drawer .coupon-code-success .ant-form-item-extra {
            color: #15803d;
          }
        `}
      </style>
      <div
        style={{
          maxWidth: isExpanded ? 720 : "100%",
          margin: isExpanded ? "0 auto" : 0,
          fontFamily: "Inter, sans-serif",
        }}
      >
        <Form
          layout="vertical"
          size="small"
          form={form}
          onFinish={handleFinish}
          onValuesChange={handleFormValuesChange}
        >
          <Row gutter={12}>
            <Col xs={24} md={isExpanded ? 12 : 24}>
              <Form.Item
                label="Code"
                name="code"
                className={
                  codeAvailability.status === "available"
                    ? "coupon-code-success"
                    : undefined
                }
                getValueFromEvent={normalizeCodeInput}
                validateStatus={
                  codeAvailability.status === "checking"
                    ? "validating"
                    : codeAvailability.status === "available"
                      ? "success"
                      : codeAvailability.status === "unavailable"
                        ? "error"
                        : undefined
                }
                help={codeAvailability.message || undefined}
                validateTrigger={["onSubmit"]}
                rules={[{ validator: validateCouponCode }]}
              >
                <Input
                  allowClear
                  maxLength={8}
                  placeholder="Optional, e.g. SAVE2026"
                  disabled={Boolean(coupon?.code)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={isExpanded ? 12 : 24}>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input placeholder="e.g. New customer welcome offer" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <Input.TextArea
              rows={2}
              placeholder="e.g. 25% off the first completed Olitask checkout"
            />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Discount Type" name="discountType" rules={[{ required: true }]}>
                <Select
                  placeholder="Choose percentage or fixed amount"
                  onChange={setDiscountType}
                  options={[
                    { value: "PERCENTAGE", label: "Percentage" },
                    { value: "FIXED_AMOUNT", label: "Fixed Amount" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={discountType === "PERCENTAGE" ? "Discount (%)" : "Discount Amount"}
                name="discountValue"
                rules={[{ required: true }]}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <InputNumber
                    min={0}
                    placeholder={discountType === "PERCENTAGE" ? "e.g. 25" : "e.g. 10.00"}
                    style={{ width: "100%" }}
                  />
                  {discountType === "PERCENTAGE" ? <span className="span-addon">%</span> : null}
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Currency" name="currency">
                <Select placeholder="e.g. USD" options={CURRENCIES} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Minimum Amount" name="minChargeAmount">
                <InputNumber min={0} placeholder="e.g. 50.00" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Max Discount" name="maxDiscountAmount">
                <InputNumber min={0} placeholder="e.g. 20.00" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Usage Limit" name="usageLimit">
                <InputNumber min={1} placeholder="e.g. 500" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Per User Limit" name="perUserLimit">
                <InputNumber min={1} placeholder="e.g. 1" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Status" name="status">
                <Select
                  placeholder="e.g. Active"
                  options={[
                    { value: "ACTIVE", label: "Active" },
                    { value: "INACTIVE", label: "Inactive" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Type"
                name="type"
                rules={[{ required: true }]}
                tooltip="System coupons are applied automatically by payment-service. User coupons must be passed in the payment request."
              >
                <Select
                  placeholder="e.g. User"
                  options={[
                    { value: "USER", label: "User" },
                    { value: "SYSTEM", label: "System" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Duration" name="duration" rules={[{ required: true }]}>
                <Select
                  placeholder="e.g. One-time"
                  options={[
                    { value: "ONE_TIME", label: "One-time" },
                    { value: "RECURRING", label: "Recurring" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24}>
              <Form.Item label="Oliveeb Product" name="app">
                <Select
                  showSearch
                  placeholder="e.g. All"
                  options={OLIVEEB_APP_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="Charge Code"
                name="chargeCode"
                tooltip="Limits this coupon to a specific charge code. Leave blank to allow it across eligible charges."
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="e.g. OLITASK_TASK_PAYMENT"
                  options={CHARGE_ITEM_CODES.map((code) => ({
                    value: code,
                    label: code,
                  }))}
                  filterOption={(input, option) =>
                    String(option?.label || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Stackable"
                name="stackable"
                valuePropName="checked"
                tooltip="Allow this coupon to be combined with other eligible discounts, coupons, credits, or promotions on the same checkout."
              >
                <Switch size="small" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Availability"
                name="availability"
                tooltip="Shareable coupons can be used by anyone who has the coupon. Restricted coupons can only be used by selected users."
              >
                <Select
                  placeholder="e.g. Shareable"
                  options={[
                    { value: "SHAREABLE", label: "Shareable" },
                    { value: "RESTRICTED", label: "Restricted users" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          {availability === "RESTRICTED" ? (
            <Form.Item
              label="Restricted Users"
              name="restrictedUserIds"
              tooltip="Search and add the users who are allowed to redeem this coupon."
              rules={[
                {
                  validator: (_, value) =>
                    Array.isArray(value) && value.length
                      ? Promise.resolve()
                      : Promise.reject(new Error("Add at least one user.")),
                },
              ]}
            >
              <Select
                mode="multiple"
                showSearch
                filterOption={false}
                loading={usersLoading}
                onSearch={handleUserSearch}
                options={userOptions}
                placeholder="Search users by name or email, e.g. jane@oliveeb.com"
              />
            </Form.Item>
          ) : null}

          <Form.Item label="Validity Period" name="validRange" rules={[{ required: true }]}>
            <RangePicker
              showTime
              placeholder={["Starts, e.g. today 9:00 AM", "Ends, e.g. month end 11:59 PM"]}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  );
}
